import React, { useEffect, useState, useRef } from 'react'
import ConfirmationModal from '../shared/ConfirmationModal';
import { useBeforeUnload, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectQcFilesData, selectUnkFilesData, selectStdFilesData, selectStdFilesResultData, selectQcFilesResultData, selectUnkFilesResultData, selectPeaksObject } from '../../lib/utilities/selector';
import { useSelector as useReduxSelector } from 'react-redux';
import ChartPreview from '../shared/ChartPreview';
import { BiExport } from "react-icons/bi";
import { Tab } from '@headlessui/react';
import { IoMdCheckmark } from "react-icons/io";
import ResizableDiv from '../shared/ResizableDiv';
import { LuBadgeCheck } from "react-icons/lu";
import Toolbox from '../../reusables/Toolbox';
import { setSelectedFile } from '../../reduxSlices/results/resultsSlice';
import saveResultsToFirebase from '../../lib/utilities/saveResultsToFirebase';
import sanitizeKeysDeep from '../../lib/utilities/sanitizeKeyDeep';
import { v4 as uuidv4 } from 'uuid';
import Loader from '../shared/Loader';

function ShowAllFiles() {
  // Save all data to Firebase once when the screen is mounted
  // Get full stdFiles, qcFiles, unkFiles from Redux
  const stdFiles = useReduxSelector((s) => s.results.stdFiles);
  const qcFiles = useReduxSelector((s) => s.results.qcFiles);
  const unkFiles = useReduxSelector((s) => s.results.unkFiles);
  const stdFilesPriority = useReduxSelector((s) => s.results.stdFilesPriority);

  const [progress, setProgress] = useState(undefined);
  const location = useLocation();
  const skipAutoSave = location.state?.skipAutoSave || false;

  useEffect(() => {
    if (skipAutoSave) return; // Skip auto-save if coming from the "View" button

    let didSave = false;
    if (!didSave) {
      setIsSaving(true);
      setProgress(0);
      saveResultsToFirebase(configId, sanitizeKeysDeep({
        areaUnderPeaks,
        areaUnderPeaksQc,
        areaUnderPeaksUnk,
        stdXPeaks,
        qcXPeaks,
        unkXPeaks,
        sigma,
        gamma,
        reportingPeaks,
        buggingPeaks,
        allReportingPeaks,
        peaksObject,
        stdFiles,
        qcFiles,
        unkFiles,
        stdFilesPriority,
      }), setProgress)
        .catch((error) => {
          // Optionally show error, but don't block UI
          console.error('Auto-save on mount failed:', error);
        })
        .finally(() => {
          setIsSaving(false);
          setProgress(undefined);
        });
      didSave = true;
    }
    // eslint-disable-next-line
  }, [skipAutoSave]);
  const areaUnderPeaks = useSelector((s) => s.results.areaUnderPeaks);
  const areaUnderPeaksQc = useSelector((s) => s.results.areaUnderPeaksQc);
  const areaUnderPeaksUnk = useSelector((s) => s.results.areaUnderPeaksUnk);
  const stdXPeaks = useSelector((s) => s.results.stdXPeaks);
  const qcXPeaks = useSelector((s) => s.results.qcXPeaks);
  const unkXPeaks = useSelector((s) => s.results.unkXPeaks);
  const sigma = useSelector((s) => s.results.sigma);
  const gamma = useSelector((s) => s.results.gamma);
  const reportingPeaks = useSelector((s) => s.results.reportingPeaks);
  const buggingPeaks = useSelector((s) => s.results.buggingPeaks);
  const allReportingPeaks = useSelector((s) => s.results.allReportingPeaks);
  const peaksObject = useSelector(selectPeaksObject);
  const navigate = useNavigate();
  // ...existing code...

  // Generate or retrieve from localStorage
  const getOrCreateConfigId = () => {
    const existing = localStorage.getItem('currentConfigId');
    if (existing) return existing;
    const newId = uuidv4();
    localStorage.setItem('currentConfigId', newId);
    return newId;
  };
  const configId = getOrCreateConfigId();

  // Track if there are unsaved changes
  const [isSaving, setIsSaving] = useState(false);
  // ...existing code...
  const hasUnsavedChanges = useRef(false);

  // Mark as dirty on any relevant state change
  useEffect(() => {
    hasUnsavedChanges.current = true;
  }, [areaUnderPeaks, areaUnderPeaksQc, areaUnderPeaksUnk, stdXPeaks, qcXPeaks, unkXPeaks, sigma, gamma, reportingPeaks, buggingPeaks, allReportingPeaks, peaksObject]);

  // Save handler for Finish button
  const handleFinish = async () => {
    setIsSaving(true);
    setProgress(0);
    try {
      await saveResultsToFirebase(configId, sanitizeKeysDeep({
        areaUnderPeaks,
        areaUnderPeaksQc,
        areaUnderPeaksUnk,
        stdXPeaks,
        qcXPeaks,
        unkXPeaks,
        sigma,
        gamma,
        reportingPeaks,
        buggingPeaks,
        allReportingPeaks,
        peaksObject,
        stdFiles,
        qcFiles,
        unkFiles,
        stdFilesPriority,
      }), setProgress, true); // skipStorage = true
      hasUnsavedChanges.current = false;
      // Do not navigate away, just save
    } catch (error) {
      alert('Error saving results: ' + error.message);
    } finally {
      setIsSaving(false);
      setProgress(undefined);
    }
  };


  // Auto-save on browser/tab close
  useBeforeUnload(
    (e) => {
      if (hasUnsavedChanges.current && !isSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
  );

  // Modal state for navigation blocking
  const [pendingNavigation, setPendingNavigation] = useState(null); // { to, options }
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Custom navigation handler for in-app navigation (sidebar/menu/buttons)
  const guardedNavigate = (to, options) => {
    if (hasUnsavedChanges.current && !isSaving) {
      setPendingNavigation({ to, options });
      setShowUnsavedModal(true);
      return;
    }
    navigate(to, options);
  };
  // Expose globally for sidebar/menu
  window.guardedNavigate = guardedNavigate;

  // Handler for confirming navigation in modal
  const handleConfirmNavigation = () => {
    setShowUnsavedModal(false);
    hasUnsavedChanges.current = false;
    if (pendingNavigation) {
      navigate(pendingNavigation.to, pendingNavigation.options);
      setPendingNavigation(null);
    }
  };

  // Handler for cancelling navigation in modal
  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  const stdFilesData = useSelector(selectStdFilesData);
  const qcFilesData = useSelector(selectQcFilesData);
  const unkFilesData = useSelector(selectUnkFilesData);
  const stdFilesRange = useSelector(selectStdFilesResultData);
  const qcFilesRange = useSelector(selectQcFilesResultData);
  const unkFilesRange = useSelector(selectUnkFilesResultData);

  const [chartData, setChartData] = useState([]);
  const [chartName, setChartName] = useState('');
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null); // 'std' | 'qc' | 'unk'
  const [chartRange, setChartRange] = useState([]);
  const [concentrations, setConcentrations] = useState(
    peaksObject.reduce((acc, _, index) => ({ ...acc, [index]: '--' }), {})
  );
  // Track which peak concentrations are being updated after baseline edits
  const [loadingPeaks, setLoadingPeaks] = useState(new Set());

  // const navigate = useNavigate() // removed duplicate
  const dispatch = useDispatch();

  // Watch for changes in Redux data for the currently selected file
  useEffect(() => {
    if (!selectedFileName || !selectedFileType) return;

    // console.log('ShowAllFiles: Redux data changed, refreshing chart for', selectedFileName, selectedFileType);

    // Get the updated data from Redux for the currently selected file
    let updatedFileData = null;
    if (selectedFileType === 'standard') {
      const stdFile = stdFilesData.find(fileObj => Object.keys(fileObj)[0] === selectedFileName);
      if (stdFile) {
        updatedFileData = Object.values(stdFile)[0];
      }
    } else if (selectedFileType === 'qualityControl') {
      const qcFile = qcFilesData.find(fileObj => Object.keys(fileObj)[0] === selectedFileName);
      if (qcFile) {
        updatedFileData = Object.values(qcFile)[0];
      }
    } else if (selectedFileType === 'unknown') {
      const unkFile = unkFilesData.find(fileObj => Object.keys(fileObj)[0] === selectedFileName);
      if (unkFile) {
        updatedFileData = Object.values(unkFile)[0];
      }
    }

    if (updatedFileData) {
      // console.log('ShowAllFiles: Updating chartData with fresh Redux data for', selectedFileName);
      setChartData(updatedFileData);
    }
  }, [selectedFileName, selectedFileType, stdFilesData, qcFilesData, unkFilesData]);

  const categories = [
    { name: "Standard Files", data: stdFilesData, key: 'standard' },
    { name: "Quality Control Files", data: qcFilesData, key: 'qualityControl' },
    { name: "Unknown Files", data: unkFilesData, key: 'unknown' }
  ];

  // Memoized updateConcentrations
  const updateConcentrations = React.useCallback((fileName, fileType) => {
    setConcentrations(prevState => {
      const updatedConcentrations = { ...prevState };
      peaksObject.forEach((peak, index) => {
        let value = '--';
        if (fileType === 'standard' && peak.stdConcentrations?.[fileName] !== undefined) {
          value = peak.stdConcentrations[fileName];
        } else if (fileType === 'qualityControl' && peak.qcConcentrations) {
          const qcEntry = peak.qcConcentrations.find(entry => entry[fileName] !== undefined);
          value = qcEntry ? qcEntry[fileName] : '--';
        } else if (fileType === 'unknown' && peak.unkConcentrations) {
          const unkEntry = peak.unkConcentrations.find(entry => entry[fileName] !== undefined);
          value = unkEntry ? unkEntry[fileName] : '--';
        }
        updatedConcentrations[index] = value;
      });
      return updatedConcentrations;
    });
  }, [peaksObject]);

  // Memoized handleRowClick
  const handleRowClick = React.useCallback((fileData, fileName, fileType) => {
    let liveFileData = fileData;
    if (fileType === 'standard') {
      const stdFile = stdFilesData.find(fileObj => Object.keys(fileObj)[0] === fileName);
      if (stdFile) {
        liveFileData = Object.values(stdFile)[0];
      }
    } else if (fileType === 'qualityControl') {
      const qcFile = qcFilesData.find(fileObj => Object.keys(fileObj)[0] === fileName);
      if (qcFile) {
        liveFileData = Object.values(qcFile)[0];
      }
    } else if (fileType === 'unknown') {
      const unkFile = unkFilesData.find(fileObj => Object.keys(fileObj)[0] === fileName);
      if (unkFile) {
        liveFileData = Object.values(unkFile)[0];
      }
    }
    setChartData(liveFileData);
    setChartName(fileName);
    setSelectedFileName(fileName);
    setSelectedFileType(fileType);
    dispatch(setSelectedFile({ name: fileName, fileType }));
    let fileRange;
    if (fileType === 'standard') {
      fileRange = stdFilesRange;
    } else if (fileType === 'qualityControl') {
      fileRange = qcFilesRange;
    } else if (fileType === 'unknown') {
      fileRange = unkFilesRange;
    }
    if (fileRange && fileRange[fileName]) {
      setChartRange(fileRange[fileName]);
    }
    updateConcentrations(fileName, fileType);
  }, [stdFilesData, qcFilesData, unkFilesData, stdFilesRange, qcFilesRange, unkFilesRange, dispatch, updateConcentrations]);

  // Initialize selection with first standard file
  useEffect(() => {
    if (selectedFileName && selectedFileType) return; // keep current selection
    if (!selectedFileName && stdFilesData.length > 0) {
      const firstFileName = Object.keys(stdFilesData[0])[0];
      handleRowClick(stdFilesData[0][firstFileName], firstFileName, 'standard');
    }
  }, [stdFilesData, qcFilesData, unkFilesData, selectedFileName, selectedFileType, handleRowClick]);

  // CRITICAL: Update concentrations when peaksObject changes in Redux
  useEffect(() => {
    if (selectedFileName && selectedFileType) {
      updateConcentrations(selectedFileName, selectedFileType);
    }
  }, [peaksObject, selectedFileName, selectedFileType, updateConcentrations]);

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }


  const renderFileNames = (filesData, fileType) => {
    return (
      <div className="flex flex-wrap gap-4">
        {filesData.map((fileObj, index) => {
          const fileName = Object.keys(fileObj)[0];
          // console.log(fileName)
          const fileData = Object.values(fileObj)[0];
          const isSelected = selectedFileName === fileName;

          return (
            <div
              key={index}
              className={`w-[200px] border p-3 cursor-pointer rounded-lg shadow-sm ${isSelected ? "bg-customeOrange border-collapse" : "hover:bg-gray-200 bg-gray-50"
                }`}
              onClick={() => handleRowClick(fileData, fileName, fileType)}
            >
              <div className={`flex items-center justify-between text-sm  ${isSelected ? "text-white" : "text-gray-500"}`}>{fileName} {isSelected && <IoMdCheckmark className='text-lg' />}</div>
            </div>
          );
        })}
      </div>
    );
  };


  // Keep concentrations row in sync when peaksObject or selection changes
  useEffect(() => {
    if (selectedFileName && selectedFileType) {
      updateConcentrations(selectedFileName, selectedFileType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peaksObject, selectedFileName, selectedFileType]);

  // Listen for baseline processing events to show loading state
  useEffect(() => {
    const handleConcentrationStart = (event) => {
      const { peakIndex, fileName: eventFileName } = event.detail;
      if (eventFileName === selectedFileName) {
        setLoadingPeaks(prev => new Set([...prev, peakIndex]));
      }
    };

    const handleConcentrationComplete = (e) => {
      const { peakIndex } = e.detail;
      setLoadingPeaks(prev => {
        const next = new Set(prev);
        next.delete(peakIndex);
        return next;
      });
    };

    window.addEventListener('concentration-update-start', handleConcentrationStart);
    window.addEventListener('concentration-update-complete', handleConcentrationComplete);

    return () => {
      window.removeEventListener('concentration-update-start', handleConcentrationStart);
      window.removeEventListener('concentration-update-complete', handleConcentrationComplete);
    };
  }, [selectedFileName]);



  const handleExport = () => {
    // Define columns, using the real peak names from the `name` property
    const columns = ["", ...peaksObject.map(peak => peak.name)];
    const rows = [];

    // Helper function to extract concentrations for qc and unk files
    function getConcentration(concentrationsArray, key) {
      if (!Array.isArray(concentrationsArray)) return null;
      for (const item of concentrationsArray) {
        if (key in item) {
          return item[key];
        }
      }
      return null; // Return null if the key is not found
    }

    // Populate rows with data from stdFilesData, qcFilesData, and unkFilesData
    [stdFilesData, qcFilesData, unkFilesData].forEach((fileData, dataIndex) => {
      const prefixes = ["std", "qc", "unk"];
      const prefix = prefixes[dataIndex];

      fileData.forEach(item => {
        const key = Object.keys(item)[0];
        const row = Array(columns.length).fill(""); // Initialize row with empty strings
        row[0] = key; // Set the first column with the file key

        // Populate peak data for the row
        peaksObject.forEach((peak, index) => {
          const concentrationsKey = `${prefix}Concentrations`;

          if (prefix === "std" && peak[concentrationsKey] && peak[concentrationsKey][key] !== undefined) {
            row[index + 1] = peak[concentrationsKey][key];
          } else if ((prefix === "qc" || prefix === "unk") && peak[concentrationsKey]) {
            row[index + 1] = getConcentration(peak[concentrationsKey], key);
          }
        });

        rows.push(row);
      });
    });

    // Combine headers and rows into CSV format
    const csvContent = [columns, ...rows]
      .map(row => row.join(","))
      .join("\n");

    // Trigger a download of the CSV file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "data_export.csv";
    link.click();
    URL.revokeObjectURL(url);
  };




  return (
    <>
      {isSaving && <Loader text="Saving to database..." progress={progress} />}
      <div className='flex flex-col h-full w-full bg-gray-50'>
        <ConfirmationModal
          isOpen={showUnsavedModal}
          onClose={handleCancelNavigation}
          onConfirm={handleConfirmNavigation}
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to leave this page?"
          confirmText="Leave Page"
          cancelText="Stay Here"
          type="warning"
        />
        <Toolbox floating={true} />
        <ResizableDiv initialHeight={500} minHeight={400} className="rounded-sm shadow-md px-5 py-2 m-8 ">
          {chartData && <ChartPreview data={chartData} name={chartName} range={chartRange} />}
        </ResizableDiv>


        <div className="flex-grow flex w-full justify-center items-center overflow-x-auto min-w-0 px-8">
          <table className="table-fixed border-collapse w-full">
            <thead>
              <tr>
                <th className="bg-customeBlack text-white w-[150px]">Peak Name</th>
                {peaksObject.map((value, index) => (
                  <th
                    key={`name-${index}`}
                    className="w-[120px] px-4 py-2 text-center bg-cyan-500 text-white border-2 border-gray-100 whitespace-nowrap overflow-hidden">
                    {value.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="bg-customeBlack text-white font-semibold w-[150px] whitespace-nowrap overflow-hidden">
                  Concentration (mg/L)
                </td>
                {peaksObject.map((_, index) => (
                  <td
                    key={`conc-${index}`}
                    className="w-[120px] px-4 py-2 text-center border-2 whitespace-nowrap overflow-hidden text-ellipsis">
                    <div className="font-bold text-cyan-900 flex items-center justify-center">
                      {loadingPeaks.has(index) ? (
                        <div className="inline-flex items-center space-x-1">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyan-600"></div>
                          <span className="text-xs text-cyan-600">Updating...</span>
                        </div>
                      ) : (
                        concentrations[index] !== '--' && !isNaN(parseFloat(concentrations[index]))
                          ? parseFloat(concentrations[index]).toFixed(4)
                          : concentrations[index]
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex justify-center items-center mb-24">
          <div className="w-full  mx-6 px-2 py-8 flex flex-col">
            <Tab.Group>
              <Tab.List className="flex space-x-1 rounded-lg bg-cyan-500 p-1">
                {categories.map((category, index) => (
                  <Tab
                    key={index}
                    className={({ selected }) =>
                      classNames(
                        'w-full rounded-md py-2.5 text-lg font-medium leading-5',
                        // 'ring-white/60 ring-offset-2 ring-offset-cyan-500 focus:outline-none focus:ring-2',
                        selected
                          ? 'bg-white text-cyan-500 '
                          : 'text-cyan-100 hover:bg-white/[0.12] hover:text-white'
                      )
                    }
                  >
                    {category.name}
                  </Tab>
                ))}
              </Tab.List>

              <Tab.Panels className="flex-1 overflow-y-auto mt-2">
                {categories.map((category, index) => (
                  <Tab.Panel key={index}
                    className={classNames(
                      'rounded-md bg-white p-3',
                      'ring-white/60 ring-offset-2 focus:outline-none'
                    )}
                  >
                    {renderFileNames(category.data, category.key)}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>

        <div className='flex-col items-center justify-center fixed bottom-8 right-8 z-40 '>
          <button
            onClick={() => guardedNavigate('/configuration/qa')}
            className={`flex-row flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
          >QA<LuBadgeCheck className='ml-12' fontSize={21} /></button>
          <button
            onClick={() => handleExport()}
            className={`flex-row mt-2 flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
          >Export<BiExport className='ml-6' fontSize={21} /></button>
          <button
            onClick={handleFinish}
            className={`flex-row mt-2 flex items-center justify-center 
          w-40 p-3 rounded-md self-center font-semibold
          ${isSaving || !hasUnsavedChanges.current
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'}`}
            disabled={isSaving || !hasUnsavedChanges.current}
          >{isSaving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </>
  )
}

export default ShowAllFiles